import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, MessageSquare, Plus, Archive, Search,
  Mail, MailOpen, User, ChevronRight, Zap
} from 'lucide-react';

function ThreadItem({ thread, isActive, onClick }) {
  const roleColors = { player: 'text-green-400', coach: 'text-blue-400', scout: 'text-purple-400', admin: 'text-yellow-400', superadmin: 'text-yellow-400', school_club: 'text-orange-400' };
  const other = thread.other_user;
  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer border-b border-zinc-800 transition-colors hover:bg-zinc-800/50 ${isActive ? 'bg-zinc-800/70 border-l-2 border-l-primary-neon' : ''}`}
      data-testid={`thread-${thread.id}`}
    >
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center flex-shrink-0">
            <User className={`w-4 h-4 ${roleColors[other?.role] || 'text-zinc-400'}`} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{other?.full_name || 'Unknown'}</p>
            <p className="text-xs text-zinc-500 capitalize">{other?.role === 'school_club' ? 'School/Club' : other?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {thread.unread_count > 0 && (
            <Badge className="bg-primary-neon text-black border-0 text-xs px-1.5 min-w-[20px] text-center">{thread.unread_count}</Badge>
          )}
        </div>
      </div>
      <p className="text-zinc-500 text-xs truncate ml-10">{thread.last_message}</p>
      <p className="text-zinc-600 text-[10px] ml-10 mt-1">{thread.last_message_at ? new Date(thread.last_message_at).toLocaleString() : ''}</p>
    </div>
  );
}

function MessageBubble({ msg, isOwn }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`msg-${msg.id}`}>
      <div className={`max-w-[70%] ${isOwn ? 'bg-primary-neon/15 border-primary-neon/20' : 'bg-zinc-800 border-zinc-700'} border rounded-2xl px-4 py-3`}>
        <p className="text-sm text-white whitespace-pre-wrap">{msg.content}</p>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-[10px] text-zinc-500">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
          {isOwn && <span className="text-[10px] text-zinc-600">{msg.read ? 'Read' : 'Sent'}</span>}
        </div>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [filter, setFilter] = useState('active');
  const [contactSearch, setContactSearch] = useState('');
  const messagesEndRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.get(`/messages/threads?status=${filter}`);
      setThreads(res.data);
    } catch {} finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadThread = async (thread) => {
    setActiveThread(thread);
    try {
      const res = await api.get(`/messages/thread/${thread.id}`);
      setMessages(res.data.messages);
      fetchThreads();
    } catch { toast.error('Failed to load messages'); }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeThread) return;
    const otherId = activeThread.participant_ids?.find(id => id !== user.id) || activeThread.other_user?.id;
    if (!otherId) return;

    try {
      await api.post(`/messages/send?recipient_id=${otherId}&thread_id=${activeThread.id}&content=${encodeURIComponent(newMessage)}`);
      setNewMessage('');
      const res = await api.get(`/messages/thread/${activeThread.id}`);
      setMessages(res.data.messages);
      fetchThreads();
    } catch { toast.error('Failed to send message'); }
  };

  const handleNewConversation = async (contactId) => {
    try {
      await api.post(`/messages/send?recipient_id=${contactId}&content=${encodeURIComponent('Hello!')}`);
      setShowNewConversation(false);
      await fetchThreads();
      const updatedThreads = await api.get(`/messages/threads?status=active`);
      const newThread = updatedThreads.data.find(t => t.participant_ids?.includes(contactId));
      if (newThread) loadThread(newThread);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start conversation'); }
  };

  const handleArchive = async () => {
    if (!activeThread) return;
    try {
      await api.patch(`/messages/thread/${activeThread.id}/archive`);
      toast.success('Thread archived');
      setActiveThread(null);
      setMessages([]);
      fetchThreads();
    } catch { toast.error('Failed to archive'); }
  };

  const loadContacts = async () => {
    try {
      const [contactsRes, templatesRes] = await Promise.all([
        api.get('/messages/contacts'),
        api.get('/messages/templates')
      ]);
      setContacts(contactsRes.data);
      setTemplates(templatesRes.data);
    } catch (e) { console.error('Failed to load contacts/templates', e); }
  };

  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.full_name?.toLowerCase().includes(contactSearch.toLowerCase()) || c.email?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const dashboardPath = user?.role === 'player' ? '/player/dashboard' : user?.role === 'coach' ? '/coach/dashboard' : user?.role === 'scout' ? '/scout/dashboard' : user?.role === 'school_club' ? '/school/dashboard' : '/admin/dashboard';

  return (
    <div className="h-screen bg-zinc-950 text-white flex flex-col" data-testid="messages-page">
      <nav className="border-b border-zinc-800 bg-zinc-900/50 flex-shrink-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate(dashboardPath)} variant="ghost" className="text-zinc-400 hover:text-white p-2" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /></Button>
              <img src="/logo.png" alt="FutureXV" className="h-8" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold text-sm">Messages</span>
            </div>
            <span className="text-zinc-400 text-xs hidden sm:block">{user?.full_name}</span>
          </div>
        </div>
      </nav>

      <div className="flex flex-1 overflow-hidden max-w-[1400px] mx-auto w-full">
        {/* Sidebar — Thread List */}
        <div className="w-80 border-r border-zinc-800 flex flex-col flex-shrink-0" data-testid="thread-list">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <div className="flex gap-1 flex-1">
              <Button size="sm" variant={filter === 'active' ? 'default' : 'outline'} onClick={() => setFilter('active')}
                className={`h-7 text-xs flex-1 ${filter === 'active' ? 'bg-primary-neon text-black' : 'border-zinc-700 text-zinc-400'}`}>Active</Button>
              <Button size="sm" variant={filter === 'archived' ? 'default' : 'outline'} onClick={() => setFilter('archived')}
                className={`h-7 text-xs flex-1 ${filter === 'archived' ? 'bg-primary-neon text-black' : 'border-zinc-700 text-zinc-400'}`}>Archived</Button>
            </div>
            <Dialog open={showNewConversation} onOpenChange={(open) => { setShowNewConversation(open); if (open) loadContacts(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary h-7 w-7 p-0" data-testid="new-conversation-button"><Plus className="w-3.5 h-3.5" /></Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                <DialogHeader><DialogTitle className="font-heading">New Conversation</DialogTitle></DialogHeader>
                <Input placeholder="Search contacts..." value={contactSearch} onChange={e => setContactSearch(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white mb-3" data-testid="contact-search-input" />
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {filteredContacts.length === 0 ? (
                    <p className="text-zinc-500 text-sm text-center py-4">No contacts available</p>
                  ) : filteredContacts.map(c => (
                    <div key={c.id} onClick={() => handleNewConversation(c.id)}
                      className="flex items-center gap-3 p-3 rounded-lg bg-zinc-950 hover:bg-zinc-800 cursor-pointer" data-testid={`contact-${c.id}`}>
                      <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-primary-neon" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{c.full_name}</p>
                        <p className="text-xs text-zinc-500 capitalize">{c.role === 'school_club' ? 'School/Club' : c.role}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600" />
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-zinc-500 text-sm text-center py-8">Loading...</p>
            ) : threads.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
                <p className="text-zinc-500 text-sm">No conversations yet</p>
                <p className="text-zinc-600 text-xs mt-1">Start a new conversation using the + button</p>
              </div>
            ) : threads.map(t => (
              <ThreadItem key={t.id} thread={t} isActive={activeThread?.id === t.id} onClick={() => loadThread(t)} />
            ))}
          </div>
        </div>

        {/* Main — Messages View */}
        <div className="flex-1 flex flex-col" data-testid="message-view">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Mail className="w-16 h-16 mx-auto mb-4 text-zinc-800" />
                <p className="text-zinc-500 text-lg">Select a conversation</p>
                <p className="text-zinc-600 text-sm">or start a new one</p>
              </div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/30 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-primary-neon" /></div>
                  <div>
                    <p className="font-heading font-bold">{activeThread.other_user?.full_name || 'Unknown'}</p>
                    <p className="text-zinc-500 text-xs capitalize">{activeThread.other_user?.role === 'school_club' ? 'School/Club' : activeThread.other_user?.role}</p>
                  </div>
                </div>
                <Button onClick={handleArchive} variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="archive-thread-button">
                  <Archive className="w-4 h-4 mr-1" />Archive
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="messages-container">
                {messages.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">No messages yet. Say hello!</p>
                ) : messages.map(msg => (
                  <MessageBubble key={msg.id} msg={msg} isOwn={msg.sender_id === user.id} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-zinc-800 bg-zinc-900/30 flex-shrink-0" data-testid="message-input-area">
                {/* Quick templates for coaches */}
                {user?.role === 'coach' && templates.length > 0 && (
                  <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
                    {templates.map(t => (
                      <Button key={t.id} size="sm" variant="outline" onClick={() => setNewMessage(t.content)}
                        className="border-zinc-700 text-zinc-400 hover:text-white text-[10px] h-6 px-2 flex-shrink-0 whitespace-nowrap" data-testid={`template-${t.id}`}>
                        <Zap className="w-2.5 h-2.5 mr-1" />{t.label}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..."
                    className="bg-zinc-950 border-zinc-800 text-white min-h-[40px] max-h-[100px] resize-none flex-1"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    data-testid="message-input" />
                  <Button onClick={handleSend} className="btn-primary h-10 w-10 p-0 flex-shrink-0" disabled={!newMessage.trim()} data-testid="send-message-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
